/**
 * ServiceAreaRadius — service coverage map with an optional polygon geofence.
 *
 * Pass `servicePolygon` (array of [lng, lat] pairs) to draw a custom boundary.
 * Falls back to a simple radius circle approximation when no polygon is provided.
 *
 * Theme-aware: reads data-theme attribute so the CARTO basemap switches with
 * the platform's theme toggle (light = Positron, dark = Dark Matter).
 *
 * Registered as: service-area / service-area-radius
 */
import React, { useEffect, useMemo, useState } from "react";
import { Map, MapMarker, MarkerContent, MarkerTooltip, MapControls, MapGeoJSON } from "@repo/ui/map";
import type { SectionComponentProps } from "../../registry.js";
import { geocodeAddress } from "../../lib/geocode.js";
import type { Feature, Polygon } from "geojson";

interface ServiceAreaRadiusProps {
  headline?: string;
  subheadline?: string;
  /** Street address to geocode — used if lat/lon not supplied */
  address?: string;
  latitude?: number;
  longitude?: number;
  /** Service radius in miles — used only when servicePolygon is not provided (default: 20) */
  radiusMiles?: number;
  /**
   * Custom polygon boundary as an array of [longitude, latitude] pairs.
   * The polygon is automatically closed if the last point doesn't match the first.
   * When provided, this replaces the radius circle.
   */
  servicePolygon?: [number, number][];
  /** Human-readable list of served towns/ZIPs displayed as chips */
  areasServed?: string[];
  markerLabel?: string;
  zoom?: number;
}

interface Coords { lng: number; lat: number }

/** Read --color-primary from the CSS custom property, with a fallback. */
function usePrimaryColor(fallback: string): string {
  const [color, setColor] = useState(fallback);
  useEffect(() => {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue("--color-primary")
      .trim();
    if (val) setColor(val);
  }, []);
  return color;
}

/** Sync with the platform's data-theme attribute for the CARTO basemap. */
function useDataTheme(): "light" | "dark" {
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof document === "undefined") return "light";
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "dark" || attr === "light") return attr;
    return typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const attr = document.documentElement.getAttribute("data-theme");
      if (attr === "dark" || attr === "light") setTheme(attr);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return theme;
}

/** Approximate a radius circle as a 64-point polygon. */
function buildCirclePolygon(
  lng: number,
  lat: number,
  radiusMiles: number,
): [number, number][] {
  const STEPS = 64;
  const latDeg = radiusMiles / 69.0;
  const lngDeg = radiusMiles / (69.0 * Math.cos((lat * Math.PI) / 180));
  const coords: [number, number][] = [];
  for (let i = 0; i <= STEPS; i++) {
    const angle = (i / STEPS) * 2 * Math.PI;
    coords.push([lng + lngDeg * Math.cos(angle), lat + latDeg * Math.sin(angle)]);
  }
  return coords;
}

function MapPlaceholder({ geocoding }: { geocoding: boolean }) {
  return (
    <div className="flex items-center justify-center h-full min-h-64 text-muted-foreground text-sm text-center p-8 bg-muted rounded-[var(--radius)] border border-border">
      {geocoding ? (
        <div>
          <div className="flex gap-1 justify-center mb-3">
            {[0, 150, 300].map((d) => (
              <span key={d} className="block w-2 h-2 rounded-full bg-muted-foreground/50 animate-pulse" style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
          <p className="text-xs">Locating address…</p>
        </div>
      ) : (
        <div>
          <div className="text-4xl mb-3">📍</div>
          <p className="font-medium mb-1">No location set</p>
          <p className="text-xs">Add <code className="bg-background px-1 py-0.5 rounded">address</code> to section props</p>
        </div>
      )}
    </div>
  );
}

export function ServiceAreaRadius({ props }: SectionComponentProps) {
  const {
    headline = "Our Service Area",
    subheadline,
    address,
    latitude,
    longitude,
    radiusMiles = 20,
    servicePolygon,
    areasServed = [],
    markerLabel,
    zoom = 10,
  } = props as ServiceAreaRadiusProps;

  const hasExplicitCoords = latitude !== undefined && longitude !== undefined;
  const [coords, setCoords] = useState<Coords | null>(
    hasExplicitCoords ? { lng: longitude!, lat: latitude! } : null,
  );
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);

  const theme = useDataTheme();
  const primaryColor = usePrimaryColor("#AADD2E");

  useEffect(() => {
    if (hasExplicitCoords || !address || coords || geocodeFailed) return;
    let cancelled = false;
    setGeocoding(true);
    geocodeAddress(address).then((result) => {
      if (cancelled) return;
      setGeocoding(false);
      if (result) setCoords({ lng: result.longitude, lat: result.latitude });
      else setGeocodeFailed(true);
    });
    return () => { cancelled = true; };
  }, [address, hasExplicitCoords, coords, geocodeFailed]);

  const polygonCoords = useMemo<[number, number][]>(() => {
    if (servicePolygon && servicePolygon.length >= 3) {
      const ring = [...servicePolygon];
      const first = ring[0]!;
      const last = ring[ring.length - 1]!;
      if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
      return ring;
    }
    if (coords) return buildCirclePolygon(coords.lng, coords.lat, radiusMiles);
    return [];
  }, [servicePolygon, coords, radiusMiles]);

  const geofenceGeoJSON = useMemo<Feature<Polygon> | null>(() => {
    if (polygonCoords.length < 4) return null;
    return {
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [polygonCoords] },
    };
  }, [polygonCoords]);

  const tooltipLabel = markerLabel ?? `${radiusMiles} mi service area`;

  return (
    <section className="py-20 px-6 bg-background" id="service-area">
      <div className="max-w-5xl mx-auto">
        {(headline || subheadline) && (
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{headline}</h2>
            {subheadline && (
              <p className="text-muted-foreground text-base leading-relaxed max-w-xl">{subheadline}</p>
            )}
          </div>
        )}

        <div className={areasServed.length > 0 ? "grid md:grid-cols-2 gap-8 items-start" : "w-full"}>
          <div className="rounded-[var(--radius)] overflow-hidden border border-border aspect-video">
            {coords ? (
              <Map
                center={[coords.lng, coords.lat]}
                zoom={zoom}
                interactive={true}
                theme={theme}
              >
                {geofenceGeoJSON && (
                  <MapGeoJSON
                    data={geofenceGeoJSON}
                    fillPaint={{
                      "fill-color": primaryColor,
                      "fill-opacity": 0.12,
                    }}
                    linePaint={{
                      "line-color": primaryColor,
                      "line-width": 2,
                      "line-opacity": 0.75,
                    }}
                  />
                )}
                <MapMarker longitude={coords.lng} latitude={coords.lat}>
                  <MarkerContent>
                    <div className="w-4 h-4 rounded-full bg-primary border-2 border-white shadow-lg" />
                  </MarkerContent>
                  <MarkerTooltip>{tooltipLabel}</MarkerTooltip>
                </MapMarker>
                <MapControls showZoom position="bottom-right" />
              </Map>
            ) : (
              <MapPlaceholder geocoding={geocoding} />
            )}
          </div>

          {areasServed.length > 0 && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">Areas We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {areasServed.map((area) => (
                    <span
                      key={area}
                      className="bg-muted/60 border border-border rounded-full px-3.5 py-1.5 text-sm text-foreground"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We travel up to <strong>{radiusMiles} miles</strong> to serve you.
                Don&apos;t see your area? Contact us — we may still be able to help.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
